import type { QueueItemDto } from '@process-hub/shared';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { STAGE_LABEL } from '../components/ApprovalPanel';
import { describeError } from '../errors';
import { formatWhen } from '../time';

/** Versions awaiting a decision in your departments. Rows you can decide come first. */
export function Approvals() {
  const [items, setItems] = useState<QueueItemDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .approvalQueue()
      .then((q) => setItems([...q].sort((a, b) => Number(b.canDecide) - Number(a.canDecide) || a.submittedAt.localeCompare(b.submittedAt))))
      .catch((e: unknown) => setError(describeError(e)));
  }, []);

  if (error) return <div className="notice notice--error">{error}</div>;
  if (!items) return <div className="notice">Loading the queue.</div>;

  const mine = items.filter((i) => i.canDecide).length;
  return (
    <div className="card">
      <div className="card__header">
        <div className="card__title">Awaiting approval</div>
        <div className="card__meta">
          {items.length} waiting · {mine} for you
        </div>
      </div>
      {items.length === 0 ? (
        <div className="notice" style={{ margin: 18 }}>
          Nothing is waiting for approval in your departments.
        </div>
      ) : (
        <table className="grid">
          <thead>
            <tr>
              <th>Process</th>
              <th>Version</th>
              <th>Department</th>
              <th>Owner</th>
              <th>Stage</th>
              <th>Waiting since</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {items.map((q) => (
              <tr key={q.versionId} className="grid__row--link" onClick={() => navigate(`/processes/${q.slug}?v=${q.label}#approval`)} data-testid={`queue-${q.slug}`}>
                <td>
                  <div className="grid__primary">{q.processName}</div>
                  {q.automationOverride && <div className="grid__secondary">Automation override — Compliance gate applies</div>}
                </td>
                <td className="mono">v{q.label}</td>
                <td>{q.departmentName}</td>
                <td>{q.ownerName ?? <span className="muted">—</span>}</td>
                <td>{STAGE_LABEL[q.stage]}</td>
                <td className="mono">{formatWhen(q.submittedAt)}</td>
                <td>{q.canDecide ? <span className="pill pill--status-InApproval">Your decision</span> : <span className="muted">Waiting on others</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
